// This file is generated by scripts/generators/generate-usecase-table-organisms.ts
import React from 'react'
import type { UsecaseTableBsHighlight } from 'poc-ai-biz-irdb_domain-model'
import { UsecaseTable } from '../UsecaseTable'
import { UsecaseTableAccordion } from '../../UsecaseTableAccordion'

type Props = {
  contents: UsecaseTableBsHighlight[]
  status: 'pending' | 'processing' | 'completed' | 'failed'
  defaultExpanded: boolean
}

export const UsecaseTableBsHighlightOrganism: React.FC<Props> = ({ contents, status, defaultExpanded = false }) => {
  // contentsをfilerNameとfiscalYearでグルーピング
  const groupedByCompanyAndYear = contents.reduce(
    (acc, item) => {
      const companyKey = `${item.filerName || ''}_${item.secCode || ''}`
      if (!acc[companyKey]) {
        acc[companyKey] = {
          filerName: item.filerName || '',
          secCode: item.secCode || '',
          years: {},
        }
      }
      const yearKey = item.fiscalYearEndYear.toString()
      if (!acc[companyKey].years[yearKey]) {
        acc[companyKey].years[yearKey] = []
      }
      acc[companyKey].years[yearKey].push(item)
      return acc
    },
    {} as Record<
      string,
      {
        filerName: string
        secCode: string
        years: Record<string, UsecaseTableBsHighlight[]>
      }
    >,
  )

  // 年度の一覧を取得
  const allYears = new Set<number>()
  Object.values(groupedByCompanyAndYear).forEach((company) => {
    Object.keys(company.years).forEach((year) => allYears.add(parseInt(year)))
  })
  const sortedYears = Array.from(allYears).sort((a, b) => b - a)

  // 会社の一覧を取得
  const companies = Object.values(groupedByCompanyAndYear)

  const formatValue = (value: string | number | string[] | object | null | undefined) => {
    if (!value) return '-'
    if (Array.isArray(value)) {
      return (
        <ul style={{ margin: 0, paddingLeft: '1.2em' }}>
          {value.map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </ul>
      )
    }
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2)
    }
    if (typeof value === 'number') {
      return value.toLocaleString()
    }
    return value
  }

  const formatNumber = (value: number | null | undefined, unit: string = '') => {
    if (!value) return '-'
    return `${value.toLocaleString()}${unit}`
  }

  // ローディング状態の表示
  if (status === 'pending' || status === 'processing') {
    return (
      <UsecaseTableAccordion summary="貸借対照表ハイライト (連結貸借対照表)" defaultExpanded={defaultExpanded} disabled={true}>
        <span className="text-gray-500">読み込み中...</span>
      </UsecaseTableAccordion>
    )
  }

  // エラー状態の表示
  if (status === 'failed') {
    return (
      <UsecaseTableAccordion summary="貸借対照表ハイライト (連結貸借対照表)" defaultExpanded={defaultExpanded} disabled={false}>
        <span className="text-red-600">データの読み込みに失敗しました</span>
      </UsecaseTableAccordion>
    )
  }

  return (
    <UsecaseTableAccordion summary="貸借対照表ハイライト (連結貸借対照表)" defaultExpanded={defaultExpanded} disabled={false}>
      {sortedYears.length === 1 ? (
        // 年度が1つの場合、1つの表で表頭にcompanyが並ぶ
        <UsecaseTable>
          <UsecaseTable.Unit>単位: 百万円</UsecaseTable.Unit>
          <UsecaseTable.Head>
            <UsecaseTable.Row>
              <UsecaseTable.Cell type="text" component="th">
                &nbsp;
              </UsecaseTable.Cell>
              {companies.map((company, index) => (
                <UsecaseTable.Cell key={index} type="text" component="th">
                  {company.filerName}
                </UsecaseTable.Cell>
              ))}
            </UsecaseTable.Row>
          </UsecaseTable.Head>
          <UsecaseTable.Body>
            {(
              [
                { name: 'cashAndDepositsJpy', displayName: '現金及び現預金', type: 'int' },
                { name: 'accountsReceivableJpy', displayName: '売掛金', type: 'int' },
                { name: 'inventoriesJpy', displayName: '棚卸資産', type: 'int' },
                { name: 'propertyPlantEquipmentJpy', displayName: '有形固定資産', type: 'int' },
                { name: 'totalAssetsJpy', displayName: '資産合計', type: 'int' },
              ] as const
            ).map((col) => {
              const cellType = ['int', 'float'].includes(col.type) ? 'currency' : 'text'

              return (
                <UsecaseTable.Row key={col.name}>
                  <UsecaseTable.Cell type="text" component="th">
                    {col.displayName}
                  </UsecaseTable.Cell>
                  {companies.map((company, companyIndex) => {
                    const yearData = company.years[sortedYears[0]?.toString() ?? '']
                    const contentItem = yearData?.[0]
                    if (!contentItem) {
                      return (
                        <UsecaseTable.Cell key={companyIndex} type={cellType}>
                          -
                        </UsecaseTable.Cell>
                      )
                    }
                    return (
                      <UsecaseTable.Cell key={companyIndex} type={cellType}>
                        {formatValue(contentItem[col.name as keyof UsecaseTableBsHighlight])}
                      </UsecaseTable.Cell>
                    )
                  })}
                </UsecaseTable.Row>
              )
            })}
          </UsecaseTable.Body>
        </UsecaseTable>
      ) : (
        // 年度が複数の場合、companyごとに表を分ける
        <div className="flex flex-col gap-6">
          {companies.map((company, companyIndex) => (
            <div key={companyIndex} className="flex flex-col gap-2">
              <h3 className="text-sm font-bold">{company.filerName}</h3>
              <UsecaseTable>
                <UsecaseTable.Unit>単位: 百万円</UsecaseTable.Unit>
                <UsecaseTable.Head>
                  <UsecaseTable.Row>
                    <UsecaseTable.Cell type="text" component="th">
                      &nbsp;
                    </UsecaseTable.Cell>
                    {sortedYears.map((year) => (
                      <UsecaseTable.Cell key={year} type="text" component="th">
                        {year}年度
                      </UsecaseTable.Cell>
                    ))}
                  </UsecaseTable.Row>
                </UsecaseTable.Head>
                <UsecaseTable.Body>
                  {(
                    [
                      { name: 'cashAndDepositsJpy', displayName: '現金及び現預金', type: 'int' },
                      { name: 'accountsReceivableJpy', displayName: '売掛金', type: 'int' },
                      { name: 'inventoriesJpy', displayName: '棚卸資産', type: 'int' },
                      { name: 'propertyPlantEquipmentJpy', displayName: '有形固定資産', type: 'int' },
                      { name: 'totalAssetsJpy', displayName: '資産合計', type: 'int' },
                    ] as const
                  ).map((col) => {
                    const cellType = ['int', 'float'].includes(col.type) ? 'currency' : 'text'

                    return (
                      <UsecaseTable.Row key={col.name}>
                        <UsecaseTable.Cell type="text" component="th">
                          {col.displayName}
                        </UsecaseTable.Cell>
                        {sortedYears.map((year) => {
                          const yearData = company.years[year.toString()]
                          const contentItem = yearData?.[0]
                          if (!contentItem) {
                            return (
                              <UsecaseTable.Cell key={year} type={cellType}>
                                -
                              </UsecaseTable.Cell>
                            )
                          }
                          return (
                            <UsecaseTable.Cell key={year} type={cellType}>
                              {formatValue(contentItem[col.name as keyof UsecaseTableBsHighlight])}
                            </UsecaseTable.Cell>
                          )
                        })}
                      </UsecaseTable.Row>
                    )
                  })}
                </UsecaseTable.Body>
              </UsecaseTable>
            </div>
          ))}
        </div>
      )}
    </UsecaseTableAccordion>
  )
}
